import React, { useEffect, useRef } from "react";
import { SignatureAnswerFormat } from "../../../../types/Survey";
import { SurveyQuestionProps } from "../../Components/SurveyQuestion/SurveyQuestion";
import SurveyFormItem from "../../Components/SurveyFormItem/SurveyFormItem";
import { QuestionResultModel, ResultModel, ConsentSignatureResultModel as ConsentSignatureResultModel } from "../../../../types/SurveyResult";
import SignaturePad from "signature_pad";
import "./SignatureQuestion.css"
import { SurveyForm } from "../../Components";
import useTranslate from "../../../../hooks/useTranslate";
import { getSignatureDate, getTypedNameFormItems } from "../../Util/consent";

export default function (props: SurveyQuestionProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const answerFormat = props.answerFormat as SignatureAnswerFormat;
    const strings = useTranslate(["first-name-label", "last-name-label", "required-field-placeholder"]);
    const signatureResult = props.result as ConsentSignatureResultModel;

    const handleSignatureEnd = () => {
        if (signaturePadRef.current) {
            const dataURL = signaturePadRef.current.toDataURL();
            let newResult = {
                ...props.result,
                Consented: true,
                Signature: {
                    ...signatureResult.Signature,
                    HandwrittenSignatureBase64: dataURL,
                    Date: getSignatureDate(answerFormat.signatureDateFormat)
                }
            } as ConsentSignatureResultModel;
            newResult.invalid = !answerIsValid(newResult);
            props.onResultChange(newResult);
        }
    }

    // Always invoke the latest handler so the endStroke listener never closes over
    // a stale onResultChange (which would revert sibling form items on the step).
    const handleSignatureEndRef = useRef(handleSignatureEnd);
    handleSignatureEndRef.current = handleSignatureEnd;

    useEffect(() => {
        if (canvasRef.current && !signaturePadRef.current) {
            signaturePadRef.current = new SignaturePad(canvasRef.current, {});
            if (signatureResult?.Signature?.HandwrittenSignatureBase64) {
                signaturePadRef.current.fromDataURL(signatureResult.Signature.HandwrittenSignatureBase64);
            }
        }

        const listener = () => handleSignatureEndRef.current();
        signaturePadRef.current?.addEventListener("endStroke", listener);
        return () => {
            signaturePadRef.current?.removeEventListener("endStroke", listener);
        };
    }, []);

    const nameChildResults: ResultModel[] = [
        {
            Type: "QuestionResult",
            Identifier: "FirstName",
            Answers: [signatureResult?.Signature?.GivenName || ""]
        } as QuestionResultModel,
        {
            Type: "QuestionResult",
            Identifier: "LastName",
            Answers: [signatureResult?.Signature?.FamilyName || ""]
        } as QuestionResultModel
    ];

    function answerIsValid(signatureResult: ConsentSignatureResultModel) {
        if (answerFormat.requiresName && !(signatureResult?.Signature?.GivenName && signatureResult?.Signature?.FamilyName)) {
            return false;
        }
        if (answerFormat.requiresSignatureImage && !signatureResult?.Signature?.HandwrittenSignatureBase64) {
            return false;
        }
        return true;
    }

    return <SurveyFormItem
        label={props.label}
        labelPosition={"top"}
        variant="v2"
        index={props.index}>
        {answerFormat.requiresName &&
            <SurveyForm
                suppressDesignerFormItemSelection
                formItems={getTypedNameFormItems(strings)}
                results={nameChildResults}
                onResultChange={(e) => {
                    let newResult = {
                        ...props.result,
                        Consented: true,
                        Signature: {
                            ...signatureResult.Signature,
                            GivenName: e.Identifier == "FirstName" ? (e as QuestionResultModel).Answers[0] || "" : signatureResult?.Signature?.GivenName || "",
                            FamilyName: e.Identifier == "LastName" ? (e as QuestionResultModel).Answers[0] || "" : signatureResult?.Signature?.FamilyName || "",
                            Date: getSignatureDate(answerFormat.signatureDateFormat)
                        }
                    } as ConsentSignatureResultModel;
                    newResult.invalid = !answerIsValid(newResult);
                    props.onResultChange(newResult);
                }}
                onEnterPressed={() => { }}
                contentItemSettings={{}}
                variant="v2" />
        }
        {answerFormat.requiresSignatureImage &&
            <SurveyFormItem className="handwritten-signature-question"
                bottomBorder
                topBorder
                label={answerFormat.handwrittenSignatureText ?? ""}
                labelPosition={"top"}
                variant="v2">
                <div className="handwritten-signature-container">
                    <canvas className="handwritten-signature-line" ref={canvasRef}></canvas>
                    <button
                        className="handwritten-signature-clear-button"
                        onClick={() => {
                            if (signaturePadRef.current) {
                                signaturePadRef.current.clear();
                                props.onResultChange(
                                    {
                                        ...props.result,
                                        invalid: true,
                                        Signature: {
                                            ...signatureResult.Signature,
                                            HandwrittenSignatureBase64: undefined
                                        }
                                    } as ConsentSignatureResultModel);
                            }
                        }}>Clear
                    </button>
                </div>
            </SurveyFormItem>
        }
    </SurveyFormItem>
}